/**
 * Centralized product URL resolver.
 *
 * Environment detection priority:
 *   1. VITE_ENV env var (set explicitly in .env files)
 *   2. Vite's import.meta.env.DEV / PROD (set by Vite based on mode)
 *   3. Fallback: window.location.hostname check
 *
 * Port map (ensure vite configs match):
 *   auth.podus.org          → 3000
 *   chat.podus.ai            → 3001
 *   podus.ai (landing)       → 3010
 *   harmlist.com             → 3002
 *   podus.org                → 3003
 *   checker.podus.ai        → 3004
 *   iris.citizensgavel.org   → 3005
 *   app.podus.ai             → 3006
 *   contract.podus.ai        → 3019  (the new client app; app.podus.ai serves it on 3006 until cut-over)
 *   appointment.podus.ai     → 3007  (the new client app; app.podus.ai serves it on 3006 until cut-over)
 *   multi-door.podus.org     → 3011  (legacy domain)
 *   multidoor.podus.ai       → 3011  (canonical)
 *   app.podus.org            → 3008
 *   ab.podus.ai (ambassador) → 3013
 *   dashboard.podus.org      → 3015  (Podus Aid, complainants)
 *   admin.podus.org          → 3016  (Podus Admin)
 *   lawyer.podus.org         → 3017  (Podus for Lawyers)
 *   company.podus.org        → 3018  (Podus for Companies)
 *   api.podus.ai             → 6006  (the AI service, NEW_PODUS_AI; getAiServiceUrl)
 */

function getImportMetaEnv(): Record<string, unknown> | undefined {
    try {
        return (import.meta as unknown as { env?: Record<string, unknown> }).env ?? undefined;
    } catch {
        return undefined;
    }
}

function detectEnv(): 'local' | 'production' {
    const env = getImportMetaEnv();

    // 1. Explicit env var override (set VITE_ENV=local or VITE_ENV=production in .env)
    if (env) {
        const explicit = env.VITE_ENV;
        if (explicit === 'local' || explicit === 'production') return explicit;
    }

    // 2. Vite's built-in mode detection (DEV = true when running `vite dev`)
    if (env) {
        if (env.DEV) return 'local';
        if (env.PROD) return 'production';
    }

    // 3. Fallback: hostname check
    if (
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ) {
        return 'local';
    }

    return 'production';
}

/** Lazy env — evaluated on first access so Vite's replacements and window.location are both available. */
let _env: 'local' | 'production' | undefined;
function getEnv() {
    if (!_env) _env = detectEnv();
    return _env;
}
function getIsDev() {
    return getEnv() === 'local';
}

const LOCAL_PORT_MAP: Record<string, number> = {
    'auth.podus.org': 3000,
    'podus.ai': 3010,
    'chat.podus.ai': 3001,
    'harmlist.com': 3002,
    'podus.org': 3003,
    'www.podus.org': 3003,
    'checker.podus.ai': 3004,
    'iris.citizensgavel.org': 3005,
    'ipvp.citizensgavel.org': 3005,
    'app.podus.ai': 3006,
    'contract.podus.ai': 3019,
    // The new client app (CLIENT_APPS_PLAN.md); app.podus.ai still serves it on 3006 until cut-over
    'appointment.podus.ai': 3007,
    'multi-door.podus.org': 3011,
    'multidoor.podus.ai': 3011,
    'app.podus.org': 3008,
    'help.podus.org': 3009,
    'ab.podus.ai': 3013,
    'dashboard.podus.org': 3015,
    'admin.podus.org': 3016,
    'lawyer.podus.org': 3017,
    'company.podus.org': 3018,
    // Backend, not a frontend app — mapped here so `getApiUrl()` follows the
    // same dev/prod resolution as everything else. 9000 matches NEW_PODUS_API's
    // `config/default.json`.
    'api-v2.podus.org': 9000,
    // The AI service (NEW_PODUS_AI), for `getAiServiceUrl()`
    'api.podus.ai': 6006,
};

/**
 * Resolve a production URL to its local equivalent when running in dev.
 *
 * @example
 *   resolveProductUrl("https://chat.podus.ai")
 *   // dev  → "http://localhost:3001"
 *   // prod → "https://chat.podus.ai"
 */
export function resolveProductUrl(productionUrl: string): string {
    if (!getIsDev()) return productionUrl;

    try {
        const parsed = new URL(productionUrl);
        const port = LOCAL_PORT_MAP[parsed.hostname];
        if (port) {
            return `http://localhost:${port}${parsed.pathname === '/' ? '' : parsed.pathname}${parsed.search}${parsed.hash}`;
        }
    } catch {
        // not a valid URL — return as-is
    }

    return productionUrl;
}

/** Shorthand for the auth service URL — resolved lazily at access time. */
export function getAuthUrl(): string {
    return getIsDev() ? 'http://localhost:3000' : 'https://auth.podus.org';
}

/**
 * NEW_PODUS_API base URL, always ending in `/api`.
 *
 * Apps previously each hardcoded `https://api-v2.podus.org/api` as their
 * fallback, so a dev build with no `.env` silently talked to production. This
 * resolves to `http://localhost:9000/api` in dev instead. An explicit
 * `VITE_PODUS_API_URL` still wins wherever a caller reads it first.
 */
export function getApiUrl(): string {
    return resolveProductUrl('https://api-v2.podus.org/api');
}

/**
 * The AI service (NEW_PODUS_AI): `http://localhost:6006` in dev, `https://api.podus.ai` otherwise. Its contract
 * summary and questions are websockets, so swap the scheme for `ws`/`wss` there. An explicit
 * `VITE_PODUS_AI_URL` wins wherever a caller reads it first.
 */
export function getAiServiceUrl(): string {
    return resolveProductUrl('https://api.podus.ai');
}

export const AUTH_URL = 'https://auth.podus.org';

/** Named product URL map — lazy getters so env is resolved at access time, not module init. */
export const PRODUCT_URLS = {
    get podusai() {
        return resolveProductUrl('https://podus.ai');
    },
    get chatpodusai() {
        return resolveProductUrl('https://chat.podus.ai');
    },
    /**
     * The complainant dashboard. Admins use admin.podus.org, companies and their lawyers company.podus.org,
     * and lawyers lawyer.podus.org once it is live (app.podus.org until then).
     */
    get podusaid() {
        return resolveProductUrl('https://dashboard.podus.org');
    },
    get podusappointment() {
        return resolveProductUrl('https://appointment.podus.ai');
    },
    get poduscontract() {
        return resolveProductUrl('https://contract.podus.ai');
    },
    get poduschecker() {
        return resolveProductUrl('https://checker.podus.org');
    },
    get podusmultidoor() {
        return resolveProductUrl('https://multidoor.podus.ai');
    },
    /** Admin and Super Admin accounts only; not listed in Explore or on public pages. */
    get podusadmin() {
        return resolveProductUrl('https://admin.podus.org');
    },
    /** Company and Company Lawyer accounts (COMPANY_APP_PLAN.md); not listed in Explore or on public pages. */
    get poduscompany() {
        return resolveProductUrl('https://company.podus.org');
    },
    /** Lawyer accounts: Podus Aid, Contract and Appointment work in one app (LAWYER_APP_PLAN.md); not live yet. */
    get poduslawyer() {
        return resolveProductUrl('https://lawyer.podus.org');
    },
    get podushelp() {
        return resolveProductUrl('https://help.podus.org');
    },
    get podus() {
        return resolveProductUrl('https://podus.org');
    },
    /** Podus Ambassador — invite-only referral platform. */
    get podusambassador() {
        return resolveProductUrl('https://ab.podus.ai');
    },
};

/**
 * Base for ambassador referral links. Always the public podus.ai host — the
 * `/ab/{code}` route lives on the marketing site, not on the ambassador
 * dashboard, so a shared link never exposes the platform's own domain.
 */
export function getReferralLinkBase(): string {
    return `${resolveProductUrl('https://podus.ai')}/ab`;
}

/** Current resolved environment. */
export { getEnv as PODUS_ENV };
